import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useMaintenanceWorkloadDetail, useMaintenanceWorkloads } from './hooks/useMaintenanceWorkload';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard } from '@/components/ui';
import { WorkloadFormModal } from './components/WorkloadFormModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  calculated: 'bg-blue-100 text-blue-600',
  reviewing: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  planned: 'bg-os-primary/10 text-os-primary',
  archived: 'bg-slate-200 text-slate-500',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  calculated: 'Đã tính toán',
  reviewing: 'Đang soát xét',
  approved: 'Đã duyệt',
  planned: 'Đã lập kế hoạch',
  archived: 'Lưu trữ',
};

export default function WorkloadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: workload, isLoading, error } = useMaintenanceWorkloadDetail(id);
  const { deleteWorkload, isDeleting, calculateWorkload, isCalculating } = useMaintenanceWorkloads(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!workload) return;
    try {
      await deleteWorkload(workload.id);
      toast.success('Đã xóa workload thành công');
      navigate('/maintenance/workload');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const handleCalculate = async () => {
    if (!id) return;
    try {
      await calculateWorkload(id);
      toast.success('Tính toán khối lượng thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải thông tin workload...</div>;
  if (!workload) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu workload</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Chi tiết khối lượng' },
    { value: 'rules', label: 'Định mức áp dụng' },
    { value: 'teams', label: 'Tổ đội đề xuất' },
    { value: 'resources', label: 'Vật tư & Thiết bị' },
    { value: 'forecast', label: 'Dự báo' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={workload.title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[workload.status]} border-transparent text-[10px] font-bold `}>
            {statusLabels[workload.status]}
          </Badge>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {workload.project?.name}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-map-pin-2-line" />
            {workload.zone?.zone_name}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {new Date(workload.period_start).toLocaleDateString('vi-VN')} - {new Date(workload.period_end).toLocaleDateString('vi-VN')}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Workload bảo dưỡng', path: '/maintenance/workload' },
        { label: workload.title }
      ]}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-calculator-line" onClick={handleCalculate} isLoading={isCalculating}>
            Tính toán khối lượng
          </Button>
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
          <AuthButton
            module="maintenance"
            action="delete"
            variant="ghost"
            className="text-red-500 hover:bg-red-50"
            icon="ri-delete-bin-line"
            onClick={() => setShowDelete(true)}
          />
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard title="Tổng điểm tải" value={workload.total_workload_score} icon="ri-dashboard-3-line" variant={workload.total_workload_score > 80 ? 'danger' : 'info'} />
          <KPICard title="Tổng giờ công" value={workload.estimated_man_hours} icon="ri-user-settings-line" variant="info" />
          <KPICard title="Giờ thiết bị" value={workload.estimated_equipment_hours} icon="ri-steering-2-line" variant="info" />
          <KPICard title="Vật tư (VNĐ)" value={workload.estimated_material_cost?.toLocaleString()} icon="ri-money-dollar-circle-line" variant="success" />
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin chung & Mục tiêu
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {workload.description || 'Không có mô tả chi tiết cho đợt tính toán khối lượng này.'}
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-list-check text-os-primary" />
                  Tóm tắt khối lượng theo loại tài sản
                </h3>
                <div className="space-y-4">
                  {workload.items?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="text-[10px] text-slate-500 font-normal bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 font-normal">Loại tài sản</th>
                            <th className="px-4 py-3 font-normal">Công việc</th>
                            <th className="px-4 py-3 text-right font-normal">Số lượng</th>
                            <th className="px-4 py-3 text-right font-normal">Tần suất</th>
                            <th className="px-4 py-3 text-right font-normal">Man-hours</th>
                            <th className="px-4 py-3 text-right font-normal">Điểm tải</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {workload.items.map(item => (
                            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 font-semibold text-slate-700  text-[10px]">{item.asset_type}</td>
                              <td className="px-4 py-3 text-slate-600 capitalize">{item.task_type}</td>
                              <td className="px-4 py-3 text-right text-slate-900 font-bold">{item.base_quantity} {item.unit}</td>
                              <td className="px-4 py-3 text-right text-slate-600">{item.frequency_value} lần/{item.frequency_unit}</td>
                              <td className="px-4 py-3 text-right text-slate-900 font-bold">{item.estimated_man_hours}h</td>
                              <td className="px-4 py-3 text-right">
                                <Badge variant="custom" className="text-[10px]">{item.workload_score}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <Alert variant="info" icon="ri-calculator-line">
                      Chưa có chi tiết khối lượng. Nhấn "Tính toán khối lượng" để hệ thống tự động quét Asset và áp định mức.
                    </Alert>
                  )}
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-team-line text-slate-400" />
                  Đề xuất tổ đội
                </h4>
                <div className="p-4 bg-os-primary/5 rounded-2xl border border-os-primary/10">
                  <p className="text-[10px] text-os-primary font-bold  mb-1">Số tổ đội đề xuất</p>
                  <p className="text-2xl font-black text-os-primary">3 <span className="text-sm font-normal">Tổ đội</span></p>
                  <p className="text-[10px] text-slate-500 mt-2">Dựa trên 160h công/tháng/người và hiệu suất 85%</p>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Nhân sự/Tổ:</span>
                    <span className="font-bold text-slate-700">4-5 người</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Tổng nhân sự:</span>
                    <span className="font-bold text-slate-700">14 người</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-share-forward-line text-slate-400" />
                  Hành động tiếp theo
                </h4>
                <div className="space-y-2">
                  <Button variant="outline" className="w-full justify-start text-xs" icon="ri-file-check-line">Gửi duyệt Workload</Button>
                  <Button variant="outline" className="w-full justify-start text-xs" icon="ri-calendar-event-line" disabled={workload.status !== 'approved'}>Chuyển thành Maintenance Plan</Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab !== 'overview' && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Phần này đang được tích hợp dữ liệu chi tiết từ các bảng định mức và dự báo tài chính."
          />
        )}
      </div>

      <WorkloadFormModal
        isOpen={showEdit}
        workload={workload}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />

      <ConfirmDialog
        isOpen={showDelete}
        title="Xác nhận xóa Workload"
        message={`Bạn có chắc chắn muốn xóa "${workload.title}"? Dữ liệu tính toán khối lượng chi tiết sẽ bị mất.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setShowDelete(false)}
      />
    </PageDetailLayout>
  );
}
